/**
 * The story tree as pure rules (1.I-1 deliverable 20): the top-level split into roots and
 * "Unassigned", the flattening of what is visible into rows, the ARIA tree pattern's keys, the
 * indentation, and which ancestors to open for the open page. No React here.
 */
import type { TreeNodeV1 } from '@codex/contracts';

/** The expanded-set key of the "Unassigned" treeitem (document ids are UUIDs, so no clash). */
export const UNASSIGNED_KEY = 'unassigned';
export const UNASSIGNED_LABEL = 'Unassigned';
export const INDENT_PX = 14;
export const MAX_INDENT_LEVEL = 8;

export interface TopLevel {
  readonly roots: readonly TreeNodeV1[];
  readonly unassigned: readonly TreeNodeV1[];
}

/** Arcs and any page with children are roots, in the answer's order; the rest are unassigned. */
export function splitTopLevel(nodes: readonly TreeNodeV1[]): TopLevel {
  const roots: TreeNodeV1[] = [];
  const unassigned: TreeNodeV1[] = [];
  for (const node of nodes) {
    if (node.typeKey === 'arc' || node.childCount > 0) roots.push(node);
    else unassigned.push(node);
  }
  return { roots, unassigned };
}

export interface TreeRow {
  /** The document id, or `UNASSIGNED_KEY`. */
  readonly key: string;
  readonly kind: 'node' | 'unassigned';
  readonly node: TreeNodeV1 | null;
  readonly level: number;
  readonly setSize: number;
  readonly posInSet: number;
  readonly parentKey: string | null;
  readonly expandable: boolean;
  readonly expanded: boolean;
  readonly firstChildKey: string | null;
}

export interface TreeData {
  readonly top: TopLevel;
  /** The loaded children of a document, by its id. */
  readonly children: ReadonlyMap<string, readonly TreeNodeV1[]>;
}

/** Every row a reader can see, top to bottom, with the numbers the ARIA pattern asks for. */
export function visibleRows(data: TreeData, expanded: ReadonlySet<string>): TreeRow[] {
  const rows: TreeRow[] = [];
  const addNodes = (nodes: readonly TreeNodeV1[], level: number, parentKey: string | null) => {
    nodes.forEach((node, index) => {
      const open = node.childCount > 0 && expanded.has(node.id);
      const kids = open ? (data.children.get(node.id) ?? []) : [];
      rows.push({
        key: node.id,
        kind: 'node',
        node,
        level,
        setSize: nodes.length,
        posInSet: index + 1,
        parentKey,
        expandable: node.childCount > 0,
        expanded: open,
        firstChildKey: kids[0]?.id ?? null,
      });
      if (open) addNodes(kids, level + 1, node.id);
    });
  };
  const hasUnassigned = data.top.unassigned.length > 0;
  const topSize = data.top.roots.length + (hasUnassigned ? 1 : 0);
  data.top.roots.forEach((node, index) => {
    const open = node.childCount > 0 && expanded.has(node.id);
    const kids = open ? (data.children.get(node.id) ?? []) : [];
    rows.push({
      key: node.id,
      kind: 'node',
      node,
      level: 1,
      setSize: topSize,
      posInSet: index + 1,
      parentKey: null,
      expandable: node.childCount > 0,
      expanded: open,
      firstChildKey: kids[0]?.id ?? null,
    });
    if (open) addNodes(kids, 2, node.id);
  });
  if (hasUnassigned) {
    const open = expanded.has(UNASSIGNED_KEY);
    rows.push({
      key: UNASSIGNED_KEY,
      kind: 'unassigned',
      node: null,
      level: 1,
      setSize: topSize,
      posInSet: topSize,
      parentKey: null,
      expandable: true,
      expanded: open,
      firstChildKey: open ? (data.top.unassigned[0]?.id ?? null) : null,
    });
    if (open) addNodes(data.top.unassigned, 2, UNASSIGNED_KEY);
  }
  return rows;
}

export type TreeAction =
  | { readonly type: 'none' }
  | { readonly type: 'focus'; readonly key: string }
  | { readonly type: 'expand'; readonly key: string }
  | { readonly type: 'collapse'; readonly key: string }
  | { readonly type: 'open'; readonly key: string };

const NONE: TreeAction = { type: 'none' };

/** Up, Down, Right, Left, Home, End and Enter, as the ARIA tree pattern defines them. */
export function treeKeys(
  rows: readonly TreeRow[],
  focusedKey: string | null,
  key: string,
): TreeAction {
  const index = rows.findIndex((row) => row.key === focusedKey);
  const row = rows[index];
  if (!row) return NONE;
  switch (key) {
    case 'ArrowDown': {
      const next = rows[index + 1];
      return next ? { type: 'focus', key: next.key } : NONE;
    }
    case 'ArrowUp': {
      const prev = rows[index - 1];
      return prev ? { type: 'focus', key: prev.key } : NONE;
    }
    case 'Home':
      return { type: 'focus', key: (rows[0] as TreeRow).key };
    case 'End':
      return { type: 'focus', key: (rows[rows.length - 1] as TreeRow).key };
    case 'ArrowRight':
      if (row.expandable && !row.expanded) return { type: 'expand', key: row.key };
      if (row.expanded && row.firstChildKey) return { type: 'focus', key: row.firstChildKey };
      return NONE;
    case 'ArrowLeft':
      if (row.expandable && row.expanded) return { type: 'collapse', key: row.key };
      if (row.parentKey) return { type: 'focus', key: row.parentKey };
      return NONE;
    case 'Enter':
      if (row.kind === 'unassigned') {
        return { type: row.expanded ? 'collapse' : 'expand', key: row.key };
      }
      return { type: 'open', key: row.key };
    default:
      return NONE;
  }
}

/** Left padding of a row: 14 px per level, no deeper than level 8. */
export function indentPx(level: number): number {
  return (Math.min(level, MAX_INDENT_LEVEL) - 1) * INDENT_PX;
}

/** Deeper than the indentation reaches: the row shows its level as text. */
export function showsLevelText(level: number): boolean {
  return level > MAX_INDENT_LEVEL;
}

/**
 * The ids to open so the page at the end of `chainIds` (root first) is visible: its ancestors,
 * and "Unassigned" when the chain's root sits there.
 */
export function ancestorsToExpand(
  chainIds: readonly string[],
  unassignedIds: ReadonlySet<string>,
): string[] {
  const ids = chainIds.slice(0, -1);
  const root = chainIds[0];
  if (root !== undefined && unassignedIds.has(root)) ids.push(UNASSIGNED_KEY);
  return ids;
}

/** The ids already open plus these, each once, the newest kept when over `cap`. */
export function mergeExpanded(
  current: readonly string[],
  add: readonly string[],
  cap: number,
): string[] {
  const merged = [...current];
  for (const id of add) if (!merged.includes(id)) merged.push(id);
  return merged.slice(-cap);
}
